/**
 * 积分扣费 Hook
 * 统一处理所有功能的扣费逻辑
 */

import { useState, useCallback } from 'react';
import { supabase } from '../src/lib/supabaseClient';
import {
  deductCredits,
  getFeatureCreditCost,
  getFeatureCreditCostAsync,
  type FeatureType,
  type DeductResult,
} from '../services/creditService';
import { recordFeatureUsage } from '../services/usageService';
import { useAccount } from '../contexts/AccountContext';

export interface UseCreditDeductionOptions {
  language?: 'zh' | 'en';
  onInsufficientBalance?: (required: number, available: number) => void;
  onDeductionSuccess?: (result: DeductResult) => void;
  onDeductionError?: (error: string) => void;
}

export interface UseCreditDeductionReturn {
  /** 是否正在扣费中 */
  isDeducting: boolean;
  /** 扣费错误信息 */
  deductError: string | null;
  /** 当前余额（与 AccountProvider 一致；无可信数据时为 null） */
  currentBalance: number | null;
  /** 执行扣费 */
  deduct: (featureType: FeatureType, description?: string) => Promise<DeductResult | null>;
  /** 刷新余额 */
  refreshBalance: () => Promise<number>;
  /** 检查余额是否充足（不实际扣费） */
  checkBalance: (featureType: FeatureType) => Promise<{ sufficient: boolean; required: number; available: number }>;
}

/**
 * 积分扣费 Hook
 */
export function useCreditDeduction(options: UseCreditDeductionOptions = {}): UseCreditDeductionReturn {
  const {
    language = 'zh',
    onInsufficientBalance,
    onDeductionSuccess,
    onDeductionError,
  } = options;

  const { remainingCredits, refreshAccount } = useAccount();

  const [isDeducting, setIsDeducting] = useState(false);
  const [deductError, setDeductError] = useState<string | null>(null);

  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch (err) {
      console.error('[useCreditDeduction] 获取用户失败:', err);
      return null;
    }
  }, []);

  const refreshBalance = useCallback(async (): Promise<number> => {
    return refreshAccount({ silent: true });
  }, [refreshAccount]);

  const checkBalance = useCallback(
    async (
      featureType: FeatureType,
    ): Promise<{ sufficient: boolean; required: number; available: number }> => {
      let required: number;
      try {
        required = await getFeatureCreditCostAsync(featureType);
      } catch (err) {
        console.error('[useCreditDeduction] GET /api/credits/plan-deduct 失败，使用本地常量估算:', err);
        required = getFeatureCreditCost(featureType);
      }
      let available = remainingCredits;
      if (available == null || !Number.isFinite(available)) {
        available = await refreshBalance();
      }
      const sufficient = available >= required;
      console.log('[CreditDeduction] checkBalance:', {
        featureType,
        required,
        available,
        sufficient,
      });
      return {
        sufficient,
        required,
        available,
      };
    },
    [refreshBalance, remainingCredits],
  );

  const deduct = useCallback(
    async (featureType: FeatureType, description?: string): Promise<DeductResult | null> => {
      setIsDeducting(true);
      setDeductError(null);

      try {
        const userId = await getCurrentUserId();
        if (!userId) {
          const error = language === 'zh' ? '未登录，请先登录' : 'Please login first';
          setDeductError(error);
          onDeductionError?.(error);
          return null;
        }

        const { sufficient, required, available } = await checkBalance(featureType);

        if (!sufficient) {
          const error =
            language === 'zh'
              ? `积分不足，需要 ${required} 积分，当前 ${available} 积分`
              : `Insufficient credits: need ${required}, have ${available}`;

          setDeductError(error);
          onInsufficientBalance?.(required, available);
          return {
            success: false,
            error: 'INSUFFICIENT_BALANCE',
            remainingBalance: available,
          };
        }

        const result = await deductCredits(featureType, description);

        if (result.success) {
          await refreshAccount({ silent: true });

          recordFeatureUsage(featureType).catch((err) => {
            console.error('[useCreditDeduction] 记录功能使用失败:', err);
          });

          onDeductionSuccess?.(result);
        } else {
          setDeductError(result.error || '扣费失败');
          onDeductionError?.(result.error || '扣费失败');
        }

        return result;
      } catch (err: unknown) {
        const error = err instanceof Error ? err.message : '扣费异常';
        console.error('[useCreditDeduction] 扣费异常:', err);
        setDeductError(error);
        onDeductionError?.(error);
        return {
          success: false,
          error,
        };
      } finally {
        setIsDeducting(false);
      }
    },
    [
      getCurrentUserId,
      checkBalance,
      language,
      onInsufficientBalance,
      onDeductionSuccess,
      onDeductionError,
      refreshAccount,
    ],
  );

  return {
    isDeducting,
    deductError,
    currentBalance: remainingCredits,
    deduct,
    refreshBalance,
    checkBalance,
  };
}

export default useCreditDeduction;
